import { constructPeripheralNameFromTypeAndTerm } from '@/pages/settings/bulk/helpers/utils';
import {
  constructApiModelIdentifier,
  postProcessModel,
  postProcessPeripheralModel
} from '@/services/financemodel/api.helper';
import { useMemo } from 'react';
import { FinanceModelTypes, PeripheralFinanceModelTypes } from '../contracts/financemodels';
import { APIError } from '../errors/APIError';
import { useAPIMutation, useAPIQuery } from '../useAPIProvider';
import { useCategories, useManufacturers } from './catalog';
import { useRegions } from './region';

const useFinanceModelsSub = () => {
  return useAPIQuery(['getFinanceModels'], async client => {
    const response = await client.financemodels.getModels();
    if (response.status !== 200) throw APIError.fromResponse(response);
    const models = response.body.items;

    return models;
  });
};

export const useFinanceModels = () => {
  const [regions] = useRegions();
  const [manufactures] = useManufacturers();
  const [categories] = useCategories();

  const [models] = useFinanceModelsSub();

  return useMemo(() => {
    const processedModels = models.map(model => postProcessModel(model, regions, manufactures, categories, models));

    // Filter out finance models of the same type (i.e. models that differ only be region), since one model will have all the pcms within.
    const filteredModels = processedModels.filter(
      (model, index, originalArr) =>
        index ===
        originalArr.findIndex(original => constructApiModelIdentifier(original) === constructApiModelIdentifier(model))
    );

    return filteredModels.sort((a, b) => a.name.localeCompare(b.name));
  }, [models, regions, categories, manufactures]);
};

export const useFinanceModel = (id: string) => {
  const [regions] = useRegions();
  const [manufactures] = useManufacturers();
  const [categories] = useCategories();
  const [models] = useFinanceModelsSub();

  return useAPIQuery(['getFinanceModel', id], async client => {
    const response = await client.financemodels.getModel({ params: { id } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return postProcessModel(response.body, regions, manufactures, categories, models);
  });
};

export interface CreateFinanceModelArgs {
  categoryId: string;
  manufacturerId: string;
  regionId: string;
  term?: number;
  value: number;
  pcmFactor?: number;
  type: FinanceModelTypes;
  isDefault?: boolean;
}

export const useCreateFinanceModel = () => {
  return useAPIMutation((client, query) => {
    return async ({
      type,
      categoryId,
      manufacturerId,
      term,
      value,
      pcmFactor,
      isDefault,
      regionId
    }: CreateFinanceModelArgs) => {
      const response = await client.financemodels.createModel({
        body: { financeModelType: type, categoryId, manufacturerId, term, value, pcmFactor, isDefault, regionId }
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getFinanceModels']);
      return response.body;
    };
  });
};

export const useCreateFinanceModels = () => {
  return useAPIMutation((client, query) => async (models: CreateFinanceModelArgs[]) => {
    const response = await client.financemodels.createModels({
      body: models.map(model => {
        return {
          financeModelType: model.type,
          categoryId: model.categoryId,
          manufacturerId: model.manufacturerId,
          term: model.term,
          value: model.value,
          pcmFactor: model.pcmFactor,
          regionId: model.regionId,
          isDefault: model.isDefault
        };
      })
    });

    if (response.status !== 200) throw APIError.fromResponse(response);

    await query.invalidateQueries(['getFinanceModels']);
    return response.body;
  });
};

export const useUpdateFinanceModels = () => {
  // Create finance models is an upset, so we can technically just use the same hook.
  // Giving it a different name so as not to alarm sensitive readers.

  return useCreateFinanceModels();
};
interface UpdateFinanceModelArgs extends CreateFinanceModelArgs {
  id: string;
}

export const useUpdateFinanceModel = () => {
  return useAPIMutation((client, query) => {
    return async ({
      id,
      type,
      categoryId,
      manufacturerId,
      term,
      value,
      pcmFactor,
      regionId,
      isDefault
    }: UpdateFinanceModelArgs) => {
      const response = await client.financemodels.updateModel({
        params: { id },
        body: { financeModelType: type, categoryId, manufacturerId, regionId, term, value, pcmFactor, isDefault }
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getFinanceModels']);
      await query.invalidateQueries(['getFinanceModel', id]);
      return response.body;
    };
  });
};

interface DeleteFinanceModelArgs {
  id: string;
}

export const useDeleteFinanceModel = () => {
  return useAPIMutation(client => {
    return async ({ id }: DeleteFinanceModelArgs) => {
      return await client.financemodels.deleteModel({ params: { id } });
    };
  });
};

export const usePeripheralFinanceModelsSub = () => {
  return useAPIQuery(['getPeripheralFinanceModels'], async client => {
    const response = await client.financemodels.getPeripheralModels();
    if (response.status !== 200) throw APIError.fromResponse(response);
    const models = response.body.items;

    return models;
  });
};

export const usePeripheralFinanceModels = () => {
  const [regions] = useRegions();
  const [models] = usePeripheralFinanceModelsSub();

  return useMemo(() => {
    const processedModels = models.map(model => postProcessPeripheralModel(model, regions, models));

    // Filter out finance models of the same type (i.e. models that differ only be region), since one model will have all the pcms within.
    const filteredModels = processedModels.filter(
      (model, index, originalArr) =>
        index ===
        originalArr.findIndex(
          original =>
            constructPeripheralNameFromTypeAndTerm(original.peripheralFinanceModelType, original.term) ===
            constructPeripheralNameFromTypeAndTerm(model.peripheralFinanceModelType, model.term)
        )
    );

    return filteredModels.sort((a, b) => a.name.localeCompare(b.name));
  }, [models, regions]);
};

export const usePeripheralFinanceModel = (id: string) => {
  const [regions] = useRegions();
  const [models] = usePeripheralFinanceModelsSub();

  return useAPIQuery(['getPeripheralFinanceModel', id], async client => {
    const response = await client.financemodels.getPeripheralModel({ params: { id } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return postProcessPeripheralModel(response.body, regions, models);
  });
};

export interface CreatePeripheralFinanceModelArgs {
  type: PeripheralFinanceModelTypes;
  term?: number;
  value: number;
  regionId: string;
  pcmFactor?: number;
  isDefault?: boolean;
}

export const useCreatePeripheralFinanceModel = () => {
  return useAPIMutation((client, query) => {
    return async ({ type, term, value, pcmFactor, isDefault, regionId }: CreatePeripheralFinanceModelArgs) => {
      const response = await client.financemodels.createPeripheralModel({
        body: { peripheralFinanceModelType: type, term, value, pcmFactor, isDefault, regionId }
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getPeripheralFinanceModels']);
      return response.body;
    };
  });
};

export const useCreatePeripheralFinanceModels = () => {
  return useAPIMutation((client, query) => async (models: CreatePeripheralFinanceModelArgs[]) => {
    const response = await client.financemodels.createPeripheralModels({
      body: models.map(model => {
        return {
          peripheralFinanceModelType: model.type,
          term: model.term,
          regionId: model.regionId,
          value: model.value,
          pcmFactor: model.pcmFactor,
          isDefault: model.isDefault
        };
      })
    });

    if (response.status !== 200) throw APIError.fromResponse(response);

    await query.invalidateQueries(['getPeripheralFinanceModels']);
    return response.body;
  });
};

export const useUpdatePeripheralFinanceModels = () => {
  // Create peripheral finance models is an upset, so we can technically just use the same hook.
  // Giving it a different name so as not to alarm sensitive readers.

  return useCreatePeripheralFinanceModels();
};

interface UpdateFinanceModelArgs extends CreateFinanceModelArgs {
  id: string;
}

interface UpdatePeripheralFinanceModelArgs extends CreatePeripheralFinanceModelArgs {
  id: string;
}

export const useUpdatePeripheralFinanceModel = () => {
  return useAPIMutation((client, query) => {
    return async ({ id, type, term, value, pcmFactor, isDefault, regionId }: UpdatePeripheralFinanceModelArgs) => {
      const response = await client.financemodels.updatePeripheralModel({
        params: { id },
        body: { peripheralFinanceModelType: type, term, value, pcmFactor, isDefault, regionId }
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getPeripheralFinanceModels']);
      await query.invalidateQueries(['getPeripheralFinanceModel', id]);
      return response.body;
    };
  });
};

interface DeletePeripheralFinanceModelArgs {
  id: string;
}

export const useDeletePeripheralFinanceModel = () => {
  return useAPIMutation(client => {
    return async ({ id }: DeletePeripheralFinanceModelArgs) => {
      return await client.financemodels.deletePeripheralModel({ params: { id } });
    };
  });
};
